import { normalizeRatios, type DivisionStyle, type SplitPatternItem } from "../../shared/api-contract";

/**
 * The v41 Custom Balance arithmetic, kept out of the tab so it can be checked
 * headlessly (`npm run verify:ring`). Both functions are pure.
 */

/** Every partition keeps at least this share — the engine's own floor. */
export const RATIO_FLOOR = 0.01;

/** The most one partition may take before another would be squeezed under the
 * floor (which the engine would silently renormalise, bouncing the slider). */
export const maxShare = (count: number) => 1 - RATIO_FLOOR * (count - 1);

/**
 * The split pattern's preset ratios, so Custom Balance opens on the balance the
 * ring already has instead of jumping to equal shares.
 *
 * ponytail: read off the pattern LABEL ("1:2", "Segment 1:1:1") because
 * `SplitPatternItem` carries no ratios; "Center Inlay" (5:1:5) has no digits and
 * opens equal. Upgrade path: add `ratios` to the served catalog item.
 */
export function presetRatios(label: string | undefined, count: number): number[] {
  const nums = label?.match(/\d+/g)?.map(Number) ?? [];
  return normalizeRatios(nums.length === count ? nums : [], count);
}

/**
 * Hold partition `index` at `share` and rescale the others proportionally.
 *
 * The result already sums to 1 with every entry on or above the floor, so the
 * engine's `normalizeRatios` is a no-op on it — which is the whole point: a
 * slider must land on the value the user dragged it to, not on whatever
 * renormalising afterwards produces.
 */
export function rebalance(ratios: readonly number[], index: number, share: number): number[] {
  const target = Math.min(Math.max(share, RATIO_FLOOR), maxShare(ratios.length));
  const rest = ratios.reduce((sum, v, k) => (k === index ? sum : sum + v), 0) || 1;
  return ratios.map((v, k) => (k === index ? target : (v * (1 - target)) / rest));
}

/**
 * A split pattern's balance — the numbers the geometry actually cuts on.
 *
 * v42 serves `ratios` on the catalog item, so this is exact for every pattern
 * including the two whose labels carry no digits ("Center Inlay" 5:1:5, "Twin
 * Inlay" 4:1:1:4). A pre-v42 viewer omits the field and we fall back to
 * `presetRatios`' label parse, which is what shipped before.
 *
 * Everything that draws or seeds a balance goes through here, so a tile, the
 * Custom Balance sliders and the ring cannot disagree.
 */
export function patternRatios(pat: SplitPatternItem | undefined, count: number): number[] {
  if (pat?.ratios?.length === count) return normalizeRatios(pat.ratios, count);
  return presetRatios(pat?.label, count);
}

/**
 * Does this split pattern's seam run ALONG the band (a "Segment" pattern) or
 * around it?
 *
 * `SplitPatternItem` carries no `direction`, but the id encodes it and is the
 * one field that cannot be localised or relabelled: the catalog's horizontal
 * entries are `2t-h-…` / `4t-h-…`, and the contract's own generator
 * (`evenSplitPatternId(n, "horizontal")`) emits `nN-h-even`. Read there rather
 * than off the label, which "Center Inlay" and "Twin Inlay" would defeat.
 *
 * ponytail: a `direction` field on the served catalog item retires this.
 */
export const isSegmentSplit = (patternId: string | undefined) => !!patternId?.includes("-h-");

/**
 * Do the partitions cut the CIRCUMFERENCE (arcs) or the band WIDTH (strips)?
 *
 * Mirror of the engine's `resolveSplitDirection`: a "Segment …" tile is always
 * cut across the width, and of the four divisions only Axial cuts arcs — the
 * pattern carries the ratio, `division` owns the axis. Everything that has to
 * know which axis a share is a share OF (the tile glyphs, the Custom Balance
 * readout) asks here, so the tab and the geometry cannot disagree.
 */
export const sectionsAreArcs = (
  division: DivisionStyle | undefined,
  patternId: string | undefined,
) => division === "axial" && !isSegmentSplit(patternId);

/** Even N-way ratios — what `setPartitionCount(n)` lands on for every n (each
 * catalog pattern with n ratios is even, as is the synthesised `nN-even`). */
export const evenRatios = (count: number) => Array.from({ length: Math.max(1, count) }, () => 1);
